import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Check } from 'lucide-react'

interface ContentScope {
  email: boolean
  drive: boolean
  calendar: boolean
  contacts: boolean
  tasks: boolean
}

interface Props {
  json: string
  onChange: (json: string) => void
  disabled?: boolean
}

const CATEGORIES: { key: keyof ContentScope; label: string }[] = [
  { key: 'email', label: 'Email' },
  { key: 'drive', label: 'OneDrive' },
  { key: 'calendar', label: 'Calendário' },
  { key: 'contacts', label: 'Contatos' },
  { key: 'tasks', label: 'Tarefas' },
]

function parse(json: string | null | undefined): ContentScope {
  if (!json) return { email: true, drive: true, calendar: true, contacts: true, tasks: true }
  try {
    const o = JSON.parse(json)
    return {
      email: !!o.email,
      drive: !!o.drive,
      calendar: !!o.calendar,
      contacts: !!o.contacts,
      // Plano antigo não tem a chave: ausente = ligado, senão tarefas nasceriam desligadas
      // em todo projeto criado antes deste recurso.
      tasks: o.tasks === undefined ? true : !!o.tasks,
    }
  } catch {
    return { email: true, drive: true, calendar: true, contacts: true, tasks: true }
  }
}

function isAll(s: ContentScope) {
  return s.email && s.drive && s.calendar && s.contacts && s.tasks
}

export function ContentScopePicker({ json, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const scope = parse(json)
  const all = isAll(scope)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  function toggleAll() {
    const newScope = all
      ? { email: false, drive: false, calendar: false, contacts: false, tasks: false }
      : { email: true, drive: true, calendar: true, contacts: true, tasks: true }
    onChange(JSON.stringify(newScope))
  }

  function toggleOne(key: keyof ContentScope) {
    const newScope = { ...scope, [key]: !scope[key] }
    onChange(JSON.stringify(newScope))
  }

  const selectedCount = CATEGORIES.filter(c => scope[c.key]).length

  return (
    <div ref={containerRef} className="relative inline-flex items-center gap-1">
      <button
        type="button"
        onClick={toggleAll}
        disabled={disabled}
        className={`text-[11px] px-2 py-0.5 rounded-md border ${all ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300'} disabled:opacity-50`}
      >
        {all ? '✓ Tudo' : 'Tudo'}
      </button>

      {!all && (
        <>
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            disabled={disabled}
            className="text-[11px] px-2 py-0.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"
          >
            {selectedCount} item{selectedCount !== 1 ? 's' : ''}
            <ChevronDown className="h-3 w-3" />
          </button>

          {open && (
            <div className="absolute top-full right-0 mt-1 z-20 bg-white border border-slate-200 rounded-md shadow-lg w-44 py-1">
              {CATEGORIES.map(c => (
                <button
                  type="button"
                  key={c.key}
                  onClick={() => toggleOne(c.key)}
                  className="w-full text-left text-xs px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2"
                >
                  <div className={`h-3.5 w-3.5 rounded border ${scope[c.key] ? 'bg-blue-600 border-blue-600' : 'border-slate-300'} flex items-center justify-center`}>
                    {scope[c.key] && <Check className="h-2.5 w-2.5 text-white" />}
                  </div>
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
